import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { missionService } from '../services/missionService';
import { CreateMissionPayload, MissionPriority } from '../types/mission';

const priorities: Array<{ value: MissionPriority; label: string }> = [
  { value: 'LOW', label: 'Baixa' },
  { value: 'MEDIUM', label: 'Media' },
  { value: 'HIGH', label: 'Alta' },
  { value: 'CRITICAL', label: 'Critica' },
];

export function MissionCreatePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreateMissionPayload>({
    title: '',
    goal: '',
    targetUnit: '',
    contextSummary: '',
    priority: 'MEDIUM',
  });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!formData.title.trim() || !formData.goal.trim()) {
      toast.error('Informe nome e objetivo da missao');
      return;
    }

    try {
      setSubmitting(true);
      const mission = await missionService.createMission({
        ...formData,
        targetValue: formData.targetValue ? Number(formData.targetValue) : undefined,
        targetUnit: formData.targetUnit?.trim() || undefined,
        contextSummary: formData.contextSummary?.trim() || undefined,
        deadline: formData.deadline || undefined,
      });

      toast.success('Missao criada com sucesso');
      navigate(`/missions/${mission.id}/command-center`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao criar missao');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header
        title="Nova Missao"
        subtitle="Comece pelo resultado que voce quer conquistar. O LeadNexia organiza o resto."
      />

      <div className="mx-auto w-full max-w-4xl p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-950">Definicao da missao</h2>
              <p className="mt-1 text-sm text-slate-500">
                Descreva o resultado de negocio. Nao pense em campanha, canal ou operacao agora.
              </p>
            </div>

            <div className="grid gap-5">
              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Nome da missao</span>
                <input
                  value={formData.title}
                  onChange={(event) => setFormData((prev) => ({ ...prev, title: event.target.value }))}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Ex.: Expandir consorcio em Brasilia"
                />
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">O que voce quer conquistar?</span>
                <textarea
                  value={formData.goal}
                  onChange={(event) => setFormData((prev) => ({ ...prev, goal: event.target.value }))}
                  rows={5}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Ex.: Vender R$ 500 mil em consorcios para oficinas mecanicas em Brasilia ate dezembro."
                />
              </label>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-950">Meta e prazo</h2>
              <p className="mt-1 text-sm text-slate-500">
                Isso ajuda o sistema a diagnosticar viabilidade, risco e priorizacao.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Valor alvo</span>
                <input
                  type="number"
                  value={formData.targetValue ?? ''}
                  onChange={(event) => setFormData((prev) => ({ ...prev, targetValue: event.target.value ? Number(event.target.value) : undefined }))}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="500000"
                />
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Unidade</span>
                <input
                  value={formData.targetUnit ?? ''}
                  onChange={(event) => setFormData((prev) => ({ ...prev, targetUnit: event.target.value }))}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="R$ em vendas"
                />
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Prazo</span>
                <input
                  type="date"
                  value={formData.deadline ?? ''}
                  onChange={(event) => setFormData((prev) => ({ ...prev, deadline: event.target.value }))}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                />
              </label>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-950">Contexto estrategico</h2>
              <p className="mt-1 text-sm text-slate-500">
                Informe publico, produto, regiao, limites ou qualquer contexto importante para o diagnostico inicial.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-[1fr_220px]">
              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Contexto</span>
                <textarea
                  value={formData.contextSummary ?? ''}
                  onChange={(event) => setFormData((prev) => ({ ...prev, contextSummary: event.target.value }))}
                  rows={6}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                  placeholder="Ex.: Produto principal consorcio. Regiao Brasilia. Publico oficinas mecanicas e concessionarias. Orcamento inicial moderado. Equipe comercial pequena."
                />
              </label>

              <label className="grid gap-2">
                <span className="text-sm font-medium text-slate-700">Prioridade</span>
                <select
                  value={formData.priority ?? 'MEDIUM'}
                  onChange={(event) => setFormData((prev) => ({ ...prev, priority: event.target.value as MissionPriority }))}
                  className="rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                >
                  {priorities.map((priority) => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </section>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => navigate('/missions')}
              className="rounded-2xl border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-2xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Criando missao...' : 'Criar missao'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
